import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import LHOverlay from "../../components/LHOverlay";
import LhCardDisplay from "../../components/LhCardDisplay";
import ExpandableImage from "../../components/ExpandableImage";
import { SparcWalkRowData } from "../../types/sparcWalk";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

interface SparcWalkModalProps {
  isOverlayVisible: boolean;
  setOverlayVisible: (visible: boolean) => void;
  selectedRowData: SparcWalkRowData | null;
  setSelectedRowData: (data: SparcWalkRowData | null) => void;
  detailedData: any;
  setDetailedData: (data: any) => void;
  isDetailLoading: boolean;
  detailError: string | null;
}

const SparcWalkModal: React.FC<SparcWalkModalProps> = ({
  isOverlayVisible,
  setOverlayVisible,
  setSelectedRowData,
  detailedData,
  setDetailedData,
  isDetailLoading,
  detailError,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // Add local loading state to handle transition states better
  const [localLoading, setLocalLoading] = useState<boolean>(true);

  // Set loading to false once we receive the data
  useEffect(() => {
    if (detailedData) {
      setLocalLoading(false);
    } else {
      setLocalLoading(true);
    }
  }, [detailedData]);
  // Helper function to render images (both Base64 and URLs) with expandable functionality
  const renderImage = (
    imageSource: string | null,
    alt: string = "Image",
    style: any = {}
  ) => {
    if (!imageSource || typeof imageSource !== 'string') {
      return null;
    }

    let imageUri: string;
    try {
      // Handle different image source formats
      if (imageSource.startsWith("data:image")) {
        imageUri = imageSource;
      } else if (imageSource.startsWith("https://") || imageSource.startsWith("http://")) {
        imageUri = imageSource;
      } else {
        // Clean any whitespace and add the data URI prefix
        const cleanBase64 = imageSource.replace(/\s/g, '');
        imageUri = `data:image/jpeg;base64,${cleanBase64}`;
      }

      return (
        <View style={{ 
          width: style?.width || "100%", 
          minHeight: style?.height || 200,
          marginBottom: 8,
        }}>
          <ExpandableImage
            imageUri={imageUri}
            alt={alt}
            style={{
              width: "100%",
              height: style?.height || 200,
              borderRadius: 4,
            }}
            containerStyle={{ 
              width: "100%",
              height: style?.height || 200,
            }}
          />
        </View>
      );
    } catch (error) {
      console.error("Error rendering image:", error);
      return (
        <View style={[styles.errorImageContainer, style]}>
          <Text style={[styles.errorImageText, isDark && { color: colors.text.primary }]}>Unable to display image</Text>
        </View>
      );
    }
  };

  // Render content based on loading/error state
  const renderContent = () => {
    // Show loading view if we're still fetching details
    if (isDetailLoading || localLoading) {
      return (
        <View
          style={[
            styles.loadingOverlayContainer,
            isDark && { backgroundColor: colors.background.page },
          ]}
        >
          <ActivityIndicator size="large" color={isDark ? colors.brand.primary : "#0066CC"} />
          <Text style={[styles.loadingTitle, isDark && { color: colors.text.primary }]}>Loading SPARC Walk Details...</Text>
        </View>
      );
    }

    // Show error message if there was an error
    if (detailError) {
      return (
        <View style={[styles.messageContainer, isDark && { backgroundColor: colors.background.page }]}>
          <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>{detailError}</Text>
        </View>
      );
    }

    // No data available
    if (!detailedData?.walkData) {
      return (
        <View style={[styles.messageContainer, isDark && { backgroundColor: colors.background.page }]}>
          <Text style={[styles.infoText, isDark && { color: colors.text.secondary }]}> 
            No data available for this observation.
          </Text>
        </View>
      );
    }

    // Render the walk details when data is available
    return (
      <ScrollView
        style={[
          styles.scrollContainer,
          isDark && { backgroundColor: colors.background.page },
        ]}
      >
        <View style={styles.cardContainer}>
          <View
            style={[
              styles.cardGridItemFullWidth,
              isDark && {
                borderColor: colors.border.default,
                backgroundColor: colors.background.surface,
              },
            ]}
          >
            <LhCardDisplay
              headerTitle="Conversed With"
              displayValue={detailedData.walkData.conversedWith || ""}
              width="100%"
            />
          </View>
          <View
            style={[
              styles.cardGridItemFullWidth,
              isDark && {
                borderColor: colors.border.default,
                backgroundColor: colors.background.surface,
              },
            ]}
          >
            <LhCardDisplay
              headerTitle="Expected Answers"
              displayValue={detailedData.walkData.sparcExpectedAnswers || ""}
              width="100%"
            />
          </View>
          <View
            style={[
              styles.cardGridItemFullWidth,
              isDark && {
                borderColor: colors.border.default,
                backgroundColor: colors.background.surface,
              },
            ]}
          >
            <LhCardDisplay
              headerTitle="Observations"
              displayValue={detailedData.walkData.observations || ""}
              width="100%"
            />
          </View>
          <View
            style={[
              styles.cardGridItemFullWidth,
              isDark && {
                borderColor: colors.border.default,
                backgroundColor: colors.background.surface,
              },
            ]}
          >
            <LhCardDisplay
              headerTitle="Possible Solutions"
              displayValue={detailedData.walkData.sparcSolutions || ""}
              width="100%"
            />
          </View>

          {/* Miscellaneous Observations Card */}
          <View
            style={[
              styles.fullWidthCard,
              isDark && {
                borderColor: colors.border.default,
                backgroundColor: colors.background.surface,
              },
            ]}
          >
            <View
              style={[
                styles.card,
                {
                  borderRadius: 8,
                  shadowColor: "#000",
                  shadowOpacity: 0.05,
                  shadowRadius: 4,
                  elevation: 2,
                },
                isDark && {
                  backgroundColor: colors.background.surface,
                  borderColor: colors.border.default,
                },
              ]}
            >
              <View
                style={[
                  styles.lhCardHeader,
                  isDark && {
                    backgroundColor: colors.brand.primary,
                    borderBottomColor: colors.brand.primary,
                  },
                ]}
              >
                <Text style={styles.lhCardHeaderText}>
                  Miscellaneous Observations
                </Text>
              </View>
              {detailedData.walkData.miscellImage ||
              detailedData.walkData.miscellImage2 ? (
                <View style={styles.miscImageContainer}>
                  {detailedData.walkData.miscellImage && (
                    <View style={styles.imageWithDescriptionContainer}>
                      {renderImage(
                        detailedData.walkData.miscellImage,
                        "Miscellaneous Image 1",
                        {
                          height: Platform.OS === "web" ? 160 : 200,
                          marginTop: 4,
                          width: Platform.OS === "web" ? "30%" : "100%",
                        }
                      )}
                      {detailedData.walkData.miscellImageDesc ? (
                        <Text style={[styles.imageDescription, isDark && { color: colors.text.primary }]}> 
                          {detailedData.walkData.miscellImageDesc}
                        </Text>
                      ) : null}
                    </View>
                  )}

                  {detailedData.walkData.miscellImage2 && (
                    <View
                      style={[
                        styles.imageWithDescriptionContainer,
                        { marginTop: 20 },
                      ]}
                    >
                      {renderImage(
                        detailedData.walkData.miscellImage2,
                        "Miscellaneous Image 2",
                        {
                          height: Platform.OS === "web" ? 160 : 200,
                          marginTop: 4,
                          width: Platform.OS === "web" ? "30%" : "100%",
                        }
                      )}
                      {detailedData.walkData.miscellImage2Desc ? (
                        <Text style={[styles.imageDescription, isDark && { color: colors.text.primary }]}> 
                          {detailedData.walkData.miscellImage2Desc}
                        </Text>
                      ) : null}
                    </View>
                  )}
                </View>
              ) : (
                <View
                  style={[
                    styles.noImageContainer,
                    isDark && {
                      backgroundColor: colors.background.elevated,
                      borderColor: colors.border.default,
                    },
                  ]}
                >
                  <Text style={[styles.noImageText, isDark && { color: colors.text.secondary }]}> 
                    No miscellaneous observations available
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
    );
  };

  return (
    <LHOverlay
      visible={isOverlayVisible}
      onDismiss={() => {
        setOverlayVisible(false);
        setSelectedRowData(null);
        setDetailedData(null);
      }}
      title="SparcWalk Details"
      size="large"
      hideFooter={true}
    >
      {renderContent()}
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  errorText: {
    fontSize: 16,
    color: "#dc3545",
    fontFamily: FontFamily.regular,
  },
  cardGridItem: {
    width: "48%",
    marginBottom: 16,
    overflow: "hidden",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 4px",
    ...(Platform.OS !== "web" && {
      borderWidth: 1,
      borderColor: "#e9ecef",
      borderRadius: 8,
      backgroundColor: "white",
    }),
  },
  cardGridItemFullWidth: {
    width: "100%",
    marginBottom: 16,
    overflow: "hidden",
    ...(Platform.OS === "web" && {
      boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 4px",
    }),
    ...(Platform.OS !== "web" && {
      borderWidth: 1,
      borderColor: "#e9ecef",
      borderRadius: 8,
      backgroundColor: "white",
    }),
  },
  cardContainer: {
    padding: 16,
    flexDirection: "column",
    gap: 16,
    // ...(Platform.OS !== 'web' && {
    //   backgroundColor: '#f8f9fa',
    // }),
  },
  // New styles for improved loading and message displays
  scrollContainer: {
    flex: 1,
    ...(Platform.OS !== "web" && {
      backgroundColor: "white",
    }),
  },
  loadingOverlayContainer: {
    flex: 1,
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 300,
  },
  loadingTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0066CC",
    marginTop: 12,
    fontFamily: FontFamily.regular,
  },
  messageContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  infoText: {
    fontSize: 16,
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  // Styles for miscellaneous observations
  fullWidthCard: {
    width: "100%",
    marginBottom: 16,
    overflow: "hidden",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 4px",
    borderRadius: 8,
    ...(Platform.OS !== "web" && {
      borderWidth: 1,
      borderColor: "#e9ecef",
      borderRadius: 8,
    }),
  },
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    // marginVertical: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    ...(Platform.OS !== "web" && {
      borderWidth: Platform.OS === "ios" ? 1 : 0,
      borderColor: "#e9ecef",
    }),
  },
  imagePreview: {
    width: "100%",
    height: 200,
    borderRadius: 4,
    // marginBottom: 8,
  },
  errorImageContainer: {
    width: "100%",
    height: 150,
    backgroundColor: "#f8d7da",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    // marginBottom: 8,
  },
  errorImageText: {
    color: "#721c24",
    fontWeight: "bold",
    fontFamily: FontFamily.regular,
  },
  lhCardHeader: {
    backgroundColor: "rgb(26, 115, 232)",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: "#005bb5",
    // marginBottom: 12,
    ...(Platform.OS === "ios" && {
      borderWidth: 1,
      borderColor: "rgb(26, 115, 232)",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 3,
    }),
  },
  lhCardHeaderText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 18,
    letterSpacing: 0.5,
    fontFamily: FontFamily.regular,
  },
  miscImageContainer: {
    padding: 12,
    marginBottom: 10,
  },
  imageWithDescriptionContainer: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    alignItems: Platform.OS === "web" ? "flex-start" : "center",
    justifyContent: "flex-start",
  },
  imageDescription: {
    fontSize: 16,
    color: "#FFFFFF",
    marginTop: 8,
    marginLeft: Platform.OS === "web" ? 12 : 0,
    // fontStyle: "italic",
    padding: 4,
    flex: Platform.OS === "web" ? 1 : 0,
    fontFamily: FontFamily.regular,
  },
  noImageContainer: {
    width: "100%",
    height: 150,
    backgroundColor: "#f8f9fa",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    // marginBottom: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
    borderStyle: "dashed",
  },
  noImageText: {
    color: "#FFFFFF",
    fontFamily: FontFamily.regular,
  },
});

export default SparcWalkModal;
